import {
  ArrowRight, ArrowUpRight, AudioWaveform, Blocks, Bot, Braces, ChartNoAxesCombined,
  Check, ChevronDown, ChevronUp, Database, LoaderCircle, LockKeyhole, LogOut,
  MonitorPlay, Pencil, Plus, Radio, Rocket, ScanSearch, Search,
  Settings, ShieldCheck, Sparkles, Trash2, X,
} from "lucide-react";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { AxiomLink, ICON_NAMES, IconName, LinkDraft } from "./types";

const iconMap = {
  waveform: AudioWaveform,
  "shield-check": ShieldCheck,
  "scan-search": ScanSearch,
  sparkles: Sparkles,
  database: Database,
  "monitor-play": MonitorPlay,
  "chart-no-axes-combined": ChartNoAxesCombined,
  radio: Radio,
  braces: Braces,
  blocks: Blocks,
  bot: Bot,
  rocket: Rocket,
} satisfies Record<IconName, typeof AudioWaveform>;

const iconLabels: Record<IconName, string> = {
  waveform: "오디오", "shield-check": "품질", "scan-search": "검색",
  sparkles: "비주얼", database: "데이터", "monitor-play": "미디어",
  "chart-no-axes-combined": "분석", radio: "라이브", braces: "개발",
  blocks: "모듈", bot: "AI", rocket: "실행",
};

const colors = ["#d6ff63", "#76e4c4", "#7db7ff", "#bfa3ff", "#ff9e7d", "#ffd166"];
const emptyDraft: LinkDraft = {
  name: "", url: "https://", description: "", icon: "rocket", color: colors[0],
};

async function api<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...options,
    headers: { "Content-Type": "application/json", ...options?.headers },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.message || "요청을 처리하지 못했습니다.");
  return body as T;
}

function Modal({ title, eyebrow, onClose, children, size = "normal" }: {
  title: string; eyebrow: string; onClose: () => void; children: React.ReactNode; size?: "normal" | "wide";
}) {
  useEffect(() => {
    const close = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [onClose]);

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <section className={`modal ${size === "wide" ? "modal-wide" : ""}`} role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <div className="modal-head">
          <div><span className="eyebrow">{eyebrow}</span><h2 id="modal-title">{title}</h2></div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="닫기"><X size={19} /></button>
        </div>
        {children}
      </section>
    </div>
  );
}

function LoginModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: (username: string) => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault(); setSubmitting(true); setError("");
    try {
      const result = await api<{ username: string }>("/api/auth/login", {
        method: "POST", body: JSON.stringify({ username, password }),
      });
      onSuccess(result.username);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "로그인에 실패했습니다."); }
    finally { setSubmitting(false); }
  }

  return (
    <Modal title="관리자 로그인" eyebrow="ADMIN ACCESS" onClose={onClose}>
      <div className="login-symbol"><LockKeyhole size={26} /><span>등록된 운영 계정으로 로그인하세요.</span></div>
      <form className="form-stack" onSubmit={submit}>
        <label>아이디<input autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} required /></label>
        <label>비밀번호<input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required /></label>
        {error && <p className="form-error" role="alert">{error}</p>}
        <button className="primary-button" disabled={submitting}>{submitting ? <LoaderCircle className="spin" size={18} /> : <ArrowRight size={18} />}관리 화면 열기</button>
      </form>
    </Modal>
  );
}

function LinkEditor({ initial, onClose, onSaved }: {
  initial?: AxiomLink; onClose: () => void; onSaved: (link: AxiomLink) => void;
}) {
  const [draft, setDraft] = useState<LinkDraft>(initial ?? emptyDraft);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault(); setSubmitting(true); setError("");
    try {
      const saved = await api<AxiomLink>(initial ? `/api/links/${initial.id}` : "/api/links", {
        method: initial ? "PUT" : "POST", body: JSON.stringify(draft),
      });
      onSaved(saved);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "저장하지 못했습니다."); }
    finally { setSubmitting(false); }
  }

  return (
    <Modal title={initial ? "바로가기 수정" : "새 바로가기"} eyebrow="SERVICE DETAILS" onClose={onClose}>
      <form className="form-stack" onSubmit={submit}>
        <label>메뉴 이름<input maxLength={40} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="예: STT Studio" required /></label>
        <label>연결 주소<input type="url" value={draft.url} onChange={(e) => setDraft({ ...draft, url: e.target.value })} placeholder="https://stt.example.com" required /></label>
        <label>한 줄 설명<input maxLength={100} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} placeholder="서비스가 하는 일을 짧게 적어주세요" /></label>
        <fieldset><legend>아이콘</legend><div className="icon-picker">
          {ICON_NAMES.map((name) => { const Icon = iconMap[name]; return <button key={name} className={draft.icon === name ? "selected" : ""} type="button" onClick={() => setDraft({ ...draft, icon: name })} aria-label={iconLabels[name]} title={iconLabels[name]}><Icon size={20} /></button>; })}
        </div></fieldset>
        <fieldset><legend>강조 색상</legend><div className="color-picker">
          {colors.map((color) => <button key={color} type="button" className={draft.color === color ? "selected" : ""} style={{ "--swatch": color } as React.CSSProperties} onClick={() => setDraft({ ...draft, color })} aria-label={`${color} 색상`}>{draft.color === color && <Check size={14} />}</button>)}
        </div></fieldset>
        {error && <p className="form-error" role="alert">{error}</p>}
        <div className="form-actions"><button className="text-button" type="button" onClick={onClose}>취소</button><button className="primary-button" disabled={submitting}>{submitting ? <LoaderCircle className="spin" size={18} /> : <Check size={18} />}저장하기</button></div>
      </form>
    </Modal>
  );
}

function AdminModal({ username, links, onClose, onCreate, onEdit, onDelete, onMove, onLogout }: {
  username: string; links: AxiomLink[]; onClose: () => void; onCreate: () => void;
  onEdit: (link: AxiomLink) => void; onDelete: (link: AxiomLink) => void;
  onMove: (link: AxiomLink, direction: -1 | 1) => void; onLogout: () => void;
}) {
  return (
    <Modal title="바로가기 관리" eyebrow={`${username.toUpperCase()} · ADMIN`} onClose={onClose} size="wide">
      <div className="admin-toolbar"><p>홈에 노출할 서비스와 링크를 관리합니다.</p><button className="primary-button compact" onClick={onCreate}><Plus size={17} />신규 등록</button></div>
      <div className="admin-list">
        {links.length === 0 ? <div className="admin-empty">아직 등록된 바로가기가 없습니다.</div> : links.map((link, index) => { const Icon = iconMap[link.icon]; return (
          <article className="admin-row" key={link.id}>
            <span className="admin-icon" style={{ background: link.color }}><Icon size={19} /></span>
            <div><strong>{link.name}</strong><span>{link.url}</span></div>
            <div className="order-actions">
              <button type="button" onClick={() => onMove(link, -1)} disabled={index === 0} aria-label={`${link.name} 위로 이동`} title="위로 이동"><ChevronUp size={16} /></button>
              <button type="button" onClick={() => onMove(link, 1)} disabled={index === links.length - 1} aria-label={`${link.name} 아래로 이동`} title="아래로 이동"><ChevronDown size={16} /></button>
            </div>
            <div className="row-actions"><button className="icon-button" onClick={() => onEdit(link)} aria-label={`${link.name} 수정`}><Pencil size={17} /></button><button className="icon-button danger" onClick={() => onDelete(link)} aria-label={`${link.name} 삭제`}><Trash2 size={17} /></button></div>
          </article>
        ); })}
      </div>
      <div className="admin-footer"><span>변경 내용은 서버 저장소에 즉시 반영됩니다.</span><button className="logout-button" onClick={onLogout}><LogOut size={16} />로그아웃</button></div>
    </Modal>
  );
}

export default function App() {
  const [links, setLinks] = useState<AxiomLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [adminUser, setAdminUser] = useState<string | null>(null);
  const [panel, setPanel] = useState<"login" | "admin" | "editor" | "delete" | null>(null);
  const [selected, setSelected] = useState<AxiomLink | undefined>();
  const [toast, setToast] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([
      api<AxiomLink[]>("/api/links"),
      api<{ authenticated: boolean; username?: string }>("/api/auth/session"),
    ]).then(([savedLinks, session]) => {
      setLinks(savedLinks); if (session.authenticated) setAdminUser(session.username ?? null);
    }).catch((caught) => setError(caught instanceof Error ? caught.message : "서비스 목록을 불러오지 못했습니다."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const visibleLinks = useMemo(() => {
    const keyword = query.trim().toLocaleLowerCase("ko");
    if (!keyword) return links;
    return links.filter((link) => `${link.name} ${link.description}`.toLocaleLowerCase("ko").includes(keyword));
  }, [links, query]);

  function openAdmin() { setPanel(adminUser ? "admin" : "login"); }
  function saved(link: AxiomLink) {
    setLinks((current) => selected ? current.map((item) => item.id === link.id ? link : item) : [...current, link]);
    setSelected(undefined); setPanel("admin"); setToast(selected ? "바로가기를 수정했습니다." : "새 바로가기를 등록했습니다.");
  }
  async function deleteSelected() {
    if (!selected) return;
    try {
      await api<{ success: true }>(`/api/links/${selected.id}`, { method: "DELETE" });
      setLinks((current) => current.filter((item) => item.id !== selected.id));
      setSelected(undefined); setPanel("admin"); setToast("바로가기를 삭제했습니다.");
    } catch (caught) { setToast(caught instanceof Error ? caught.message : "삭제하지 못했습니다."); }
  }
  async function logout() {
    await api("/api/auth/logout", { method: "POST" });
    setAdminUser(null); setPanel(null); setToast("로그아웃했습니다.");
  }
  async function moveLink(link: AxiomLink, direction: -1 | 1) {
    const currentIndex = links.findIndex((item) => item.id === link.id);
    const targetIndex = currentIndex + direction;
    if (currentIndex < 0 || targetIndex < 0 || targetIndex >= links.length) return;
    const previous = links;
    const reordered = [...links];
    [reordered[currentIndex], reordered[targetIndex]] = [reordered[targetIndex], reordered[currentIndex]];
    setLinks(reordered);
    try {
      const saved = await api<AxiomLink[]>("/api/links/order", { method: "PUT", body: JSON.stringify({ ids: reordered.map((item) => item.id) }) });
      setLinks(saved); setToast("바로가기 순서를 변경했습니다.");
    } catch (caught) {
      setLinks(previous); setToast(caught instanceof Error ? caught.message : "순서를 변경하지 못했습니다.");
    }
  }

  return (
    <div className="app-shell">
      <div className="ambient ambient-one" /><div className="ambient ambient-two" />
      <header className="site-header">
        <a className="brand" href="/" aria-label="Axiom Home"><span className="brand-mark">A</span><span><strong>AXIOM</strong><small>HOME</small></span></a>
        <div className="header-actions"><span className="live-badge"><i />SYSTEM HUB</span><button className="settings-button" onClick={openAdmin} aria-label="관리자 설정"><Settings size={19} /><span>관리</span></button></div>
      </header>

      <main>
        <section className="directory" aria-labelledby="directory-title">
          <div className="directory-head"><h1 id="directory-title">서비스 바로가기</h1>
            <label className="search-box"><Search size={18} /><span className="sr-only">서비스 검색</span><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="서비스 검색" /></label>
          </div>
          {loading ? <div className="state-card"><LoaderCircle className="spin" /><p>서비스를 불러오는 중입니다.</p></div>
            : error ? <div className="state-card error-state"><p>{error}</p><button onClick={() => window.location.reload()}>다시 시도</button></div>
            : visibleLinks.length > 0 ? <div className="service-grid">{visibleLinks.map((link, index) => { const Icon = iconMap[link.icon]; return (
              <a className="service-card" href={link.url} target="_blank" rel="noreferrer" key={link.id} style={{ "--accent": link.color, "--delay": `${index * 45}ms` } as React.CSSProperties}>
                <span className="card-index">{String(index + 1).padStart(2, "0")}</span><span className="service-icon"><Icon size={25} /></span>
                <div className="service-copy"><h3>{link.name}</h3><p>{link.description || "Axiom 서비스로 이동합니다."}</p></div>
                <span className="launch"><span>OPEN</span><ArrowUpRight size={19} /></span>
              </a>); })}</div>
            : <div className="state-card empty-state"><span className="empty-symbol"><Blocks size={27} /></span><h3>{query ? "검색 결과가 없습니다" : "첫 서비스를 연결해 주세요"}</h3><p>{query ? "다른 검색어로 찾아보세요." : "우측 상단 관리 메뉴에서 바로가기를 등록할 수 있습니다."}</p>{!query && <button onClick={openAdmin}>서비스 등록하기 <ArrowRight size={16} /></button>}</div>}
        </section>
      </main>

      <footer><span>© 2026 AXIOM AI</span><span>ONE HOME · EVERY TOOL</span></footer>
      {toast && <div className="toast"><Check size={17} />{toast}</div>}

      {panel === "login" && <LoginModal onClose={() => setPanel(null)} onSuccess={(username) => { setAdminUser(username); setPanel("admin"); setToast(`${username}님, 환영합니다.`); }} />}
      {panel === "admin" && adminUser && <AdminModal username={adminUser} links={links} onClose={() => setPanel(null)} onCreate={() => { setSelected(undefined); setPanel("editor"); }} onEdit={(link) => { setSelected(link); setPanel("editor"); }} onDelete={(link) => { setSelected(link); setPanel("delete"); }} onMove={moveLink} onLogout={logout} />}
      {panel === "editor" && <LinkEditor initial={selected} onClose={() => { setSelected(undefined); setPanel("admin"); }} onSaved={saved} />}
      {panel === "delete" && selected && <Modal title="바로가기를 삭제할까요?" eyebrow="CONFIRM DELETE" onClose={() => { setSelected(undefined); setPanel("admin"); }}><div className="delete-copy"><span className="delete-icon"><Trash2 size={22} /></span><p><strong>{selected.name}</strong> 바로가기가 홈에서 제거됩니다.<br />이 작업은 되돌릴 수 없습니다.</p></div><div className="form-actions"><button className="text-button" onClick={() => { setSelected(undefined); setPanel("admin"); }}>취소</button><button className="danger-button" onClick={deleteSelected}><Trash2 size={17} />삭제하기</button></div></Modal>}
    </div>
  );
}
